import React, {useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {cn} from '@/lib/utils'
import useIntersectionObserver from '@salesforce/retail-react-app/app/hooks/use-intersection-observer'

const Sticky = ({children, stickyTop = 0, attributes}) => {
    const wrapperRef = useRef(null)
    const placeholderRef = useRef(null)
    const sentinelRef = useRef(null)

    const [isSticky, setIsSticky] = useState(false)
    const [placeholderHeight, setPlaceholderHeight] = useState(0)
    const [stickyWidth, setStickyWidth] = useState('auto')
    const [hasInitialized, setHasInitialized] = useState(false)

    const sentinelInView = useIntersectionObserver(sentinelRef, {
        root: null,
        rootMargin: `0px`,
        threshold: 0
    })

    const updateDimensions = () => {
        if (wrapperRef.current && placeholderRef.current) {
            const wrapperHeight = wrapperRef.current.offsetHeight
            const wrapperWidth = placeholderRef?.current?.offsetWidth

            setPlaceholderHeight(wrapperHeight)
            setStickyWidth(`${wrapperWidth}px`)
        }
    }

    useEffect(() => {
        updateDimensions()
        setHasInitialized(true)

        window.addEventListener('resize', updateDimensions)
        return () => {
            window.removeEventListener('resize', updateDimensions)
        }
    }, [])

    useEffect(() => {
        if (hasInitialized) {
            setIsSticky(!sentinelInView)

            if (!sentinelInView) {
                updateDimensions()
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sentinelInView])

    return (
        <>
            <div ref={sentinelRef} className="h-px w-full" aria-hidden="true" />
            <div
                ref={placeholderRef}
                style={{height: isSticky ? placeholderHeight : 0}}
                aria-hidden="true"
            />
            <div
                ref={wrapperRef}
                className={cn('sticky-wrapper z-10', isSticky ? 'fixed' : 'relative')}
                style={{
                    top: isSticky ? stickyTop : 'auto',
                    width: isSticky ? stickyWidth : 'auto'
                }}
                {...attributes}
            >
                {children}
            </div>
        </>
    )
}

Sticky.propTypes = {
    children: PropTypes.node,
    stickyTop: PropTypes.number,
    attributes: PropTypes.object
}

export default Sticky

export const StickyWrapperDefinition = {
    component: Sticky,
    name: 'StickyWrapper',
    friendlyName: 'Sticky Wrapper',
    noWrap: false,
    canHaveChildren: true
}
